import { useEffect, useState } from 'react';
import { type AppSpecRevision, loadAppSpecRevisions, saveAppSpec } from '@/lib/app-spec';
import type { AppSpec } from '@shared/types/app-spec';

const SOURCE_LABEL: Record<AppSpecRevision['source'], string> = {
  manual: 'Mise en page enregistrée', chat: 'Demande', forge: 'Forge', restore: 'Retour arrière',
};

/** F6c: every saved layout of the app, and a way back to any of them (saved as a new version). */
export const AppSpecHistory = ({ appId, currentVersion, currentSpec, onRestored }: {
  appId: string; currentVersion: number; currentSpec: AppSpec; onRestored: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [revisions, setRevisions] = useState<AppSpecRevision[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    void loadAppSpecRevisions(appId)
      .then((value) => { if (active) setRevisions(value); })
      .catch((cause) => { if (active) setError(cause instanceof Error ? cause.message : 'Historique indisponible.'); });
    return () => { active = false; };
  }, [appId, open, currentVersion]);

  const restore = async (revision: AppSpecRevision | null) => {
    setBusy(true); setError(null);
    try {
      await saveAppSpec(appId, currentVersion, revision?.spec ?? null, 'restore',
        revision ? `Retour à la version ${revision.version}` : 'Retour à la mise en page d’origine');
      onRestored();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Retour arrière impossible.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <details open={open} onToggle={(event) => setOpen((event.target as HTMLDetailsElement).open)} className="text-sm">
      <summary className="cursor-pointer text-xs text-domino-inkSoft">Historique de la mise en page{currentVersion > 0 ? ` · version ${currentVersion}` : ''}</summary>
      {error && <p role="alert" className="mt-2 text-sm text-[var(--domino-critical)]">{error}</p>}
      {revisions && (
        <ul className="mt-2 space-y-1.5">
          {revisions.map((revision) => (
            <li key={revision.version} className="flex flex-wrap items-baseline justify-between gap-2 rounded-lg bg-domino-white px-3 py-2">
              <span className="min-w-0 text-domino-ink">
                <span className="font-medium">v{revision.version}</span> · {SOURCE_LABEL[revision.source]}{revision.request ? ` : « ${revision.request} »` : ''}
                <span className="text-xs text-domino-inkSoft"> · {new Date(revision.createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</span>
              </span>
              {revision.version !== currentVersion && (
                <button type="button" disabled={busy} onClick={() => void restore(revision)} className="text-xs text-domino-aubergine underline disabled:opacity-50">Revenir à cette version</button>
              )}
            </li>
          ))}
          {currentVersion > 0 && currentSpec && (
            <li className="px-3 py-1">
              <button type="button" disabled={busy} onClick={() => void restore(null)} className="text-xs text-domino-aubergine underline disabled:opacity-50">Revenir à la mise en page d’origine</button>
            </li>
          )}
        </ul>
      )}
    </details>
  );
};
